import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageCropDetailMediaImage from './ImageCropDetailMediaImage';
import {
  applyCropBoxResize,
  clampCropBoxToImage,
  computeZoomedImageDisplayRect,
  createDefaultDisplayCropBox,
  CROP_ADJUST_HANDLE_SIZE,
  displayCropBoxToNormalizedBbox,
  hitTestCropBox,
  normalizedBboxToDisplayCropBox,
  resolveCropBoxPixelSize,
} from '../utils/imageCropAdjustBox';
import '@/styles/ipe/workbench/ipe-workbench.css';
import '../styles/ImageCropDetailPage.css';

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;

function clampZoom(value) {
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Number(value) || 1));
}

/**
 * 图案裁剪详情 · 手动框选裁剪弹窗（保留原图，仅更新裁剪结果）。
 */
export default function ImageCropAdjustModal({
  open = false,
  title,
  originalUrl = '',
  displayUrl = '',
  imageId = '',
  initialBbox = null,
  confirming = false,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation('workbench');
  const viewportRef = useRef(null);
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const dragRef = useRef(null);
  const preservedBboxRef = useRef(null);

  const [imageRect, setImageRect] = useState(null);
  const [cropBox, setCropBox] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [imageLoadError, setImageLoadError] = useState(false);

  const resetCropBox = useCallback((bboxOverride) => {
    const viewport = viewportRef.current;
    const img = imageRef.current;
    if (!viewport || !img?.naturalWidth) return;

    const viewportWidth = Math.max(320, viewport.clientWidth);
    const viewportHeight = Math.max(240, viewport.clientHeight);
    const rect = computeZoomedImageDisplayRect(
      viewportWidth,
      viewportHeight,
      img.naturalWidth,
      img.naturalHeight,
      zoom,
    );
    setImageRect(rect);

    const bbox = bboxOverride ?? preservedBboxRef.current ?? initialBbox;
    const fromBbox = normalizedBboxToDisplayCropBox(bbox, rect);
    const nextBox = fromBbox || createDefaultDisplayCropBox(rect);
    setCropBox(clampCropBoxToImage(nextBox, rect));
  }, [initialBbox, zoom]);

  const applyZoom = useCallback((nextZoom) => {
    if (cropBox && imageRect) {
      const bbox = displayCropBoxToNormalizedBbox(cropBox, imageRect);
      if (bbox) preservedBboxRef.current = bbox;
    }
    setZoom(clampZoom(nextZoom));
  }, [cropBox, imageRect]);

  useEffect(() => {
    if (!open) {
      setImageRect(null);
      setCropBox(null);
      setIsDragging(false);
      setIsPanning(false);
      setSpaceHeld(false);
      setZoom(1);
      setImageLoadError(false);
      dragRef.current = null;
      preservedBboxRef.current = null;
      return undefined;
    }

    setImageLoadError(false);
    preservedBboxRef.current = initialBbox;

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !confirming) onClose?.();
      if (event.code === 'Space' && !event.repeat) {
        const tag = event.target?.tagName?.toLowerCase();
        if (tag !== 'input' && tag !== 'textarea' && tag !== 'button') {
          event.preventDefault();
          setSpaceHeld(true);
        }
      }
    };
    const onKeyUp = (event) => {
      if (event.code === 'Space') setSpaceHeld(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [open, confirming, onClose, initialBbox]);

  useEffect(() => {
    if (!open) return undefined;
    resetCropBox(preservedBboxRef.current ?? initialBbox);
  }, [open, zoom, resetCropBox, initialBbox]);

  useEffect(() => {
    if (!open) return undefined;

    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === 'undefined') {
      const onResize = () => resetCropBox(preservedBboxRef.current);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }

    const observer = new ResizeObserver(() => {
      resetCropBox(preservedBboxRef.current);
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [open, resetCropBox]);

  const beginPointerInteraction = useCallback((clientX, clientY, button, forcePan = false) => {
    if (!containerRef.current || confirming) return false;

    const containerRect = containerRef.current.getBoundingClientRect();
    const localX = clientX - containerRect.left;
    const localY = clientY - containerRect.top;
    const viewport = viewportRef.current;
    const shouldPan = forcePan || button === 1 || spaceHeld;

    if (!shouldPan && cropBox) {
      const handle = hitTestCropBox(localX, localY, cropBox, CROP_ADJUST_HANDLE_SIZE);
      if (handle) {
        dragRef.current = {
          mode: 'crop',
          handle,
          startX: clientX,
          startY: clientY,
          startBox: { ...cropBox },
        };
        setIsDragging(true);
        return true;
      }
    }

    if (!viewport) return false;

    dragRef.current = {
      mode: 'pan',
      startX: clientX,
      startY: clientY,
      startScrollLeft: viewport.scrollLeft,
      startScrollTop: viewport.scrollTop,
    };
    setIsPanning(true);
    return true;
  }, [cropBox, confirming, spaceHeld]);

  const handlePointerDown = useCallback((event) => {
    if (event.button !== 0 && event.button !== 1) return;
    event.preventDefault();
    beginPointerInteraction(event.clientX, event.clientY, event.button, spaceHeld);
  }, [beginPointerInteraction, spaceHeld]);

  useEffect(() => {
    if (!isDragging && !isPanning) return undefined;

    const onMove = (event) => {
      const drag = dragRef.current;
      if (!drag) return;

      if (drag.mode === 'pan') {
        const viewport = viewportRef.current;
        if (!viewport) return;
        const dx = drag.startX - event.clientX;
        const dy = drag.startY - event.clientY;
        viewport.scrollLeft = drag.startScrollLeft + dx;
        viewport.scrollTop = drag.startScrollTop + dy;
        return;
      }

      if (!imageRect) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      const next = applyCropBoxResize(drag.handle, drag.startBox, dx, dy, imageRect);
      setCropBox(next);
      const bbox = displayCropBoxToNormalizedBbox(next, imageRect);
      if (bbox) preservedBboxRef.current = bbox;
    };

    const onUp = () => {
      dragRef.current = null;
      setIsDragging(false);
      setIsPanning(false);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, isPanning, imageRect]);

  const handleWheel = useCallback((event) => {
    if (confirming) return;
    event.preventDefault();
    const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    applyZoom(zoom + delta);
  }, [applyZoom, confirming, zoom]);

  const pixelSize = resolveCropBoxPixelSize(cropBox, imageRect);

  const handleConfirm = () => {
    if (!pixelSize?.bbox || confirming) return;
    onConfirm?.(pixelSize.bbox, pixelSize);
  };

  if (!open) return null;

  const rawUrl = String(originalUrl || '').trim();
  if (!rawUrl) return null;

  const canvasWidth = imageRect?.canvasWidth ?? '100%';
  const canvasHeight = imageRect?.canvasHeight ?? '100%';

  const viewportClassName = [
    'image-crop-adjust-viewport',
    isDragging ? 'image-crop-adjust-viewport--dragging' : '',
    isPanning ? 'image-crop-adjust-viewport--panning' : '',
    spaceHeld ? 'image-crop-adjust-viewport--space-held' : '',
    zoom > 1 ? 'image-crop-adjust-viewport--zoomed' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="image-crop-adjust-modal" role="presentation">
      <div className="image-crop-adjust-modal-content image-crop-adjust-modal-content--large" role="dialog" aria-modal="true" aria-label={title || t('studioChrome.adjustCropTitle')}>
        <header className="image-crop-adjust-modal-header">
          <div className="image-crop-adjust-modal-header__main">
            <h3>{title}</h3>
            <p className="image-crop-adjust-modal-header__hint">
              {t('studioChrome.cropAdjustHint')}
            </p>
          </div>
          <button
            type="button"
            className="image-crop-adjust-modal-close"
            onClick={() => !confirming && onClose?.()}
            disabled={confirming}
            aria-label={t('studioChrome.close')}
          >
            ×
          </button>
        </header>

        <div className="image-crop-adjust-modal-body image-crop-adjust-modal-body--large">
          <div className="image-crop-adjust-toolbar">
            <div className="image-crop-adjust-zoom" aria-label={t('studioChrome.zoomLabel')}>
              <button
                type="button"
                className="image-crop-adjust-zoom__btn"
                onClick={() => applyZoom(zoom - ZOOM_STEP)}
                disabled={confirming || zoom <= ZOOM_MIN}
                aria-label={t('studioChrome.zoomOut')}
              >
                −
              </button>
              <span className="image-crop-adjust-zoom__value">{Math.round(zoom * 100)}%</span>
              <button
                type="button"
                className="image-crop-adjust-zoom__btn"
                onClick={() => applyZoom(zoom + ZOOM_STEP)}
                disabled={confirming || zoom >= ZOOM_MAX}
                aria-label={t('studioChrome.zoomIn')}
              >
                +
              </button>
              <button
                type="button"
                className="image-crop-adjust-zoom__fit"
                onClick={() => applyZoom(1)}
                disabled={confirming || zoom === 1}
              >
                {t('studioChrome.fitWindow')}
              </button>
            </div>
            {pixelSize.width > 0 && pixelSize.height > 0 ? (
              <span className="image-crop-adjust-toolbar__size">
                {t('studioChrome.outputSize')}
                {pixelSize.width}
                {' × '}
                {pixelSize.height}
              </span>
            ) : null}
          </div>

          <div
            ref={viewportRef}
            className={viewportClassName}
            onWheel={handleWheel}
            onContextMenu={(event) => event.preventDefault()}
          >
            <div
              ref={containerRef}
              className="image-crop-adjust-image-container image-crop-adjust-image-container--large"
              style={{ width: canvasWidth, height: canvasHeight }}
              onMouseDown={handlePointerDown}
            >
              {!imageRect && !imageLoadError ? (
                <div className="image-crop-adjust-loading" aria-live="polite">
                  {t('studioChrome.loadingOriginal')}
                </div>
              ) : null}
              {imageLoadError ? (
                <div className="image-crop-adjust-loading image-crop-adjust-loading--error" role="alert">
                  {t('studioChrome.originalLoadFail')}
                </div>
              ) : null}
              <div
                className="image-crop-adjust-image-frame"
                style={imageRect ? {
                  left: `${imageRect.offsetX}px`,
                  top: `${imageRect.offsetY}px`,
                  width: `${imageRect.displayWidth}px`,
                  height: `${imageRect.displayHeight}px`,
                } : {
                  // 保持可参与加载的尺寸，避免 1×1 hidden 导致长时间空白画布
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  pointerEvents: 'none',
                }}
              >
                <ImageCropDetailMediaImage
                  ref={imageRef}
                  url={rawUrl}
                  displayUrl={displayUrl}
                  imageId={imageId}
                  alt={t('studioChrome.cropOriginal')}
                  className="image-crop-adjust-image"
                  onLoad={() => {
                    setImageLoadError(false);
                    resetCropBox(preservedBboxRef.current ?? initialBbox);
                  }}
                  onError={() => setImageLoadError(true)}
                />
              </div>

              {cropBox ? (
                <div
                  className="image-crop-adjust-crop-box image-crop-adjust-crop-box--large"
                  style={{
                    left: `${cropBox.x}px`,
                    top: `${cropBox.y}px`,
                    width: `${cropBox.width}px`,
                    height: `${cropBox.height}px`,
                    cursor: isDragging ? 'grabbing' : (spaceHeld ? 'grab' : 'move'),
                  }}
                >
                  <span className="image-crop-adjust-crop-box__badge image-crop-adjust-crop-box__badge--top">
                    {t('studioChrome.selectionArea')}
                  </span>

                  <span className="image-crop-adjust-resize-handle image-crop-adjust-resize-handle--tl image-crop-adjust-resize-handle--large" />
                  <span className="image-crop-adjust-resize-handle image-crop-adjust-resize-handle--tr image-crop-adjust-resize-handle--large" />
                  <span className="image-crop-adjust-resize-handle image-crop-adjust-resize-handle--bl image-crop-adjust-resize-handle--large" />
                  <span className="image-crop-adjust-resize-handle image-crop-adjust-resize-handle--br image-crop-adjust-resize-handle--large" />
                  <span className="crop-handle-mid-left image-crop-adjust-edge-handle image-crop-adjust-edge-handle--left" aria-hidden="true" />
                  <span className="crop-handle-mid-right image-crop-adjust-edge-handle image-crop-adjust-edge-handle--right" aria-hidden="true" />
                  <span className="image-crop-adjust-edge-handle image-crop-adjust-edge-handle--top" aria-hidden="true" />
                  <span className="image-crop-adjust-edge-handle image-crop-adjust-edge-handle--bottom" aria-hidden="true" />
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <footer className="image-crop-adjust-modal-footer">
          <button
            type="button"
            className="image-crop-adjust-btn image-crop-adjust-btn--cancel"
            onClick={() => onClose?.()}
            disabled={confirming}
          >
            {t('studioChrome.cancel')}
          </button>
          <button
            type="button"
            className="image-crop-adjust-btn image-crop-adjust-btn--confirm"
            onClick={handleConfirm}
            disabled={confirming || !pixelSize?.bbox}
          >
            {confirming ? t('studioChrome.savingEllipsis') : t('studioChrome.confirmCrop')}
          </button>
        </footer>
      </div>
    </div>
  );
}
